import { ReactElement, useRef, useState } from 'react';
import { useEvent, useMedia } from 'react-use';
import { FormattedMessage } from 'react-intl';
import {
  StyledInformationContent,
  StyledInformationWrapper,
  StyledInformationButton,
  StyledIcon,
  StyledCloseButton,
  StyledInformationBlock,
  StyledInformationDescription,
  StyledFlightInformation,
} from './InformationContent.styles';
import { Icon } from '../../atoms/Icon/Icon';
import { MediaQuery } from '../../../styles/mediaQuery';
import { useExternalValue } from '../../../hooks/useExternalValue';

export function InformationContent(): ReactElement {
  const outsideData = useExternalValue('map-info-editable-items');
  const infoRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const isTablet = useMedia(MediaQuery.Medium);
  const id = 'map-info-legend';
  // eslint-disable-next-line no-restricted-properties
  const hasMessage = document.querySelector(`#${id}`)?.textContent !== ' ';
  const [isFocussed, setIsFocussed] = useState(false);
  const handleKeydown = (event: KeyboardEvent) => {
    if (infoModalOpen && event.key === 'Escape') {
      setInfoModalOpen(false);
    }
  };
  const handleModalToggle = () => {
    if (infoModalOpen) {
      setInfoModalOpen(false);
    } else {
      setInfoModalOpen(true);
    }
    setIsFocussed(false);
  };

  useEvent('keydown', (event) => handleKeydown(event as KeyboardEvent), document);

  useEvent(
    'focus',
    () => {
      setIsFocussed(true);
    },
    toggleButtonRef.current,
  );

  useEvent(
    'blur',
    () => {
      setIsFocussed(false);
    },
    toggleButtonRef.current,
  );
  return (
    <StyledInformationWrapper
      id="map-info-block"
      ref={infoRef}
      $isFocussed={isFocussed}
      $isOpen={infoModalOpen}
      $isTablet={isTablet}
      $hasMessage={hasMessage}
      initial={false}
      layout
    >
      <StyledInformationButton
        aria-label={infoModalOpen ? 'close legend' : 'open legend'}
        ref={toggleButtonRef}
        onClick={() => handleModalToggle()}
        $isOpen={infoModalOpen}
        $hasMessage={hasMessage}
        layout="position"
      >
        <Icon.Info />
        <FormattedMessage id={id} defaultMessage=" ">
          {(message) => (message === id ? <></> : <span id={id}>{message}</span>)}
        </FormattedMessage>
      </StyledInformationButton>
      <StyledCloseButton
        aria-label="close legend"
        disabled={!infoModalOpen}
        tabIndex={!infoModalOpen ? -1 : undefined}
        onClick={() => setInfoModalOpen(false)}
      >
        <Icon.Close />
      </StyledCloseButton>
      <StyledInformationContent>
        <StyledInformationBlock>
          <StyledInformationDescription id="map-info-nonstop-text">
            <FormattedMessage
              id="map-info-nonstop-label"
              description="information - Nonstop flight"
              defaultMessage="Nonstop flight"
            />
          </StyledInformationDescription>
          <StyledFlightInformation />
          <Icon.Airplane />
        </StyledInformationBlock>
        <StyledInformationBlock>
          <StyledInformationDescription id="map-info-connected-text">
            <FormattedMessage
              id="map-info-connected-label"
              defaultMessage="Connecting flight"
              description="information - Connecting flight"
            />
          </StyledInformationDescription>
          <StyledFlightInformation $connected />
          <Icon.Airplane />
        </StyledInformationBlock>
        {outsideData &&
          outsideData.map((customMessage, index) => {
            return (
              <>
                <StyledInformationDescription id={`map-info-custom-${index}`}>
                  <span id={`map-info-custom-${index}-label`}>{customMessage?.label}</span>
                </StyledInformationDescription>

                <StyledIcon src={customMessage?.icon} alt={customMessage?.label} />
              </>
            );
          })}
      </StyledInformationContent>
    </StyledInformationWrapper>
  );
}
